import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CreatePatchPropertyFacility,
  PropertyFacility as IPropertyFacility,
  PropertyFacilityMapper,
  PropertySubFacilityMapper,
} from '../../../store/propertyFacility/api/interface';
import { useFormik } from 'formik';
import {
  createPropertyFacility,
  getPropertyFacility,
  patchPropertyFacility,
} from '../../../store/propertyFacility/api';
import { setStatus } from '../../../store/propertyFacility/propertyFacilitySlice';
import React, { useEffect, useState } from 'react';
import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';

import propertyFacilitySchema from '../../../validation/propertyFacilitySchema';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';

import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';
import MultipleAutoComplete from '../../../components/MultipleAutoComplete';
import {
  findSelectPropertyCategory,
  searchSelectPropertyCategory,
} from '../../../store/selectPropertyCategory/api';
import {
  setSkip as selectPropertyCategorySetStatus,
  reset as resetSelectPropertyCategoryReset,
  setStatus as setSelectPropertyCategoryStatus,
} from '../../../store/selectPropertyCategory/selectPropertyCategorySlice';
import { PropertyCategory } from '../../../store/propertyCategory/api/interface';
import { RiUploadCloud2Fill } from 'react-icons/ri';
import { PropertySubCategory } from '../../../store/propertySubCategory/api/interface';
import {
  findSelectPropertySubCategory,
  searchSelectPropertySubCategory,
} from '../../../store/selectPropertySubCategory/api';
import {
  setSkip as selectPropertySubCategorySetSkip,
  reset as resetSelectPropertySubCategoryReset,
  setStatus as setSelectPropertySubCategoryStatus,
} from '../../../store/selectPropertySubCategory/selectPropertySubCategorySlice';
import { isValidArray } from '../../../utils/valid';

const PropertyFacilityForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IPropertyFacility; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.propertyFacility); // const { accessToken } = useAppSelector((state) => state.auth);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [propertyCategorys, setPropertyCategorys] = useState<
    PropertyCategory[]
  >([]);
  const [propertyCategoryIdsKey, setPropertyCategoryIdsKey] = useState<
    string | undefined
  >();
  const [fileName, setFileName] = useState<string>('');
  const [propertySubCategorys, setPropertySubCategorys] = useState<
    PropertySubCategory[]
  >([]);
  const [propertySubCategoryIdsKey, setPropertySubCategoryIdsKey] = useState<
    string | undefined
  >();

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
  } = useFormik<CreatePatchPropertyFacility>({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      subLabelEn: '',
      subLabelAr: '',
      icon: '',
      position: '',
      propertyCategoryIds: [],
      propertySubCategoryIds: [],
      activeState: true,
    },
    validationSchema: propertyFacilitySchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createPropertyFacility(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.propertyFacilityMapper;
          delete reqBody.propertyFacilitySubCategoryMapper;
          await dispatch(patchPropertyFacility(reqBody)).unwrap();
        }
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        navigation('/property-facility');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        const propertyCategoryIds =
          state.propertyFacilityMapper?.map(
            (item) => item.propertyCategoryId ?? ''
          ) || [];
        const propertySubCategoryIds =
          state.propertyFacilitySubCategoryMapper?.map(
            (item) => item.propertySubCategoryId ?? ''
          ) || [];
        setValues({
          ...state,
          propertyCategoryIds: propertyCategoryIds,
          propertySubCategoryIds: propertySubCategoryIds,
        });
        setPropertyCategorys(
          state.propertyFacilityMapper?.map(
            (propertyFacility) => propertyFacility.property_category
          )
        );
        setPropertyCategoryIdsKey(
          `pcids-${state?.propertyFacilityMapper?.length}`
        );
        setPropertySubCategorys(
          state.propertyFacilitySubCategoryMapper?.map(
            (propertySubFacility) => propertySubFacility.property_sub_category
          )
        );
        setPropertySubCategoryIdsKey(
          `pscids-${state?.propertyFacilitySubCategoryMapper?.length}`
        );
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getPropertyFacility({ id: decodeParam })
          ).unwrap();
          const propertyCategoryIds =
            record.propertyFacilityMapper?.map(
              (item: PropertyFacilityMapper) => item.propertyCategoryId ?? ''
            ) || [];
          const propertySubCategoryIds =
            record.propertyFacilitySubCategoryMapper?.map(
              (item: PropertySubFacilityMapper) =>
                item.propertySubCategoryId ?? ''
            ) || [];
          setValues({
            ...record,
            propertyCategoryIds: propertyCategoryIds,
            propertySubCategoryIds: propertySubCategoryIds,
          });
          setPropertyCategorys(
            record.propertyFacilityMapper?.map(
              (propertyFacility: PropertyFacilityMapper) =>
                propertyFacility.property_category
            )
          );
          setPropertyCategoryIdsKey(
            `pcids-${record?.propertyFacilityMapper?.length}`
          );
          setPropertySubCategorys(
            record.propertyFacilitySubCategoryMapper?.map(
              (propertySubFacility: PropertySubFacilityMapper) =>
                propertySubFacility.property_sub_category
            )
          );
          setPropertySubCategoryIdsKey(
            `pscids-${record?.propertyFacilitySubCategoryMapper?.length}`
          );
        } catch (error: any) {
          navigation('/property-facility');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, setValues, state]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
        setFileName(event.target.files?.[0]?.name);
      }
    }
  };

  // Property Category
  const {
    data: propertyCategoryIdsData,
    isLoading: isPropertyCategoryIdsLoading,
    skip: skipPropertyCategoryIds,
    hasMore: hasMorePropertyCategoryIds,
    status: propertyCategoryIdsStatus,
  } = useAppSelector((state) => state.selectPropertyCategory);

  useEffect(() => {
    if (propertyCategoryIdsStatus === 'idle') {
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: skipPropertyCategoryIds,
          activeOnly: true,
        })
      );
    }
  }, [propertyCategoryIdsStatus, skipPropertyCategoryIds]);

  const onScrollFetchPropertyCategoryIds = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyCategoryIds
    ) {
      dispatch(
        selectPropertyCategorySetStatus({ skip: skipPropertyCategoryIds + 10 })
      );
      dispatch(setSelectPropertyCategoryStatus({ status: 'idle' }));
      // dispatch(
      //   findSelectPropertyCategory({
      //     limit: 10,
      //     skip: skipPropertyCategoryIds + 10,
      //     activeOnly: true,
      //   })
      // );
    }
  };

  const searchPropertyCategoryIds = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetSelectPropertyCategoryReset());
      return;
    }
    const params = {
      activeOnly: true,
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectPropertyCategory(params));
  };

  const uniquePropertyCategoryIdsData = uniqBy(
    [
      ...(propertyCategorys?.length > 0 ? [...propertyCategorys] : []),
      ...propertyCategoryIdsData,
    ],
    'id'
  );

  // Property Sub Category
  const {
    data: propertySubCategoryIdsData,
    isLoading: isPropertySubCategoryIdsLoading,
    skip: skipPropertySubCategoryIds,
    hasMore: hasMorePropertySubCategoryIds,
    status: propertySubCategoryIdsStatus,
  } = useAppSelector((state) => state.selectPropertySubCategory);

  useEffect(() => {
    if (propertySubCategoryIdsStatus === 'idle') {
      dispatch(
        findSelectPropertySubCategory({
          limit: 10,
          skip: skipPropertySubCategoryIds,
          activeOnly: true,
        })
      );
    }
  }, [propertySubCategoryIdsStatus, skipPropertySubCategoryIds]);

  const onScrollFetchPropertySubCategoryIds = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertySubCategoryIds
    ) {
      dispatch(
        selectPropertySubCategorySetSkip({
          skip: skipPropertySubCategoryIds + 10,
        })
      );
      dispatch(setSelectPropertySubCategoryStatus({ status: 'idle' }));
    }
  };

  const searchPropertySubCategoryIds = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetSelectPropertySubCategoryReset());
      return;
    }
    const params = {
      activeOnly: true,
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectPropertySubCategory(params));
  };

  const uniquePropertySubCategoryIdsData = uniqBy(
    [
      ...(propertySubCategorys?.length > 0 ? [...propertySubCategorys] : []),
      ...propertySubCategoryIdsData,
    ],
    'id'
  );

  return (
    <Grid
      container
      spacing={3}
      mt={1.5}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <Card sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: {
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  },
                }}
              >
                <TextField
                  fullWidth
                  label={'Label English'}
                  name='labelEn'
                  id='labelEn'
                  onChange={handleChange}
                  value={values.labelEn}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={
                    Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                  }
                />
                <TextField
                  fullWidth
                  label={'Label Arabic'}
                  name='labelAr'
                  id='labelAr'
                  onChange={handleChange}
                  value={values.labelAr}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={
                    Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                  }
                />

                <TextField
                  fullWidth
                  label={'Sub Label English'}
                  name='subLabelEn'
                  id='subLabelEn'
                  onChange={handleChange}
                  value={values.subLabelEn}
                  error={Boolean(errors.subLabelEn && touched.subLabelEn)}
                  helperText={
                    Boolean(errors.subLabelEn && touched.subLabelEn) &&
                    errors.subLabelEn
                  }
                />
                <TextField
                  fullWidth
                  label={'Sub Label Arabic'}
                  name='subLabelAr'
                  id='subLabelAr'
                  onChange={handleChange}
                  value={values.subLabelAr}
                  error={Boolean(errors.subLabelAr && touched.subLabelAr)}
                  helperText={
                    Boolean(errors.subLabelAr && touched.subLabelAr) &&
                    errors.subLabelAr
                  }
                />

                <TextField
                  fullWidth
                  label={'Position'}
                  name='position'
                  id='position'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.position}
                  error={Boolean(errors.position && touched.position)}
                  helperText={
                    Boolean(errors.position && touched.position) &&
                    errors?.position
                  }
                />

                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={values?.icon ? 8.5 : 12}
                    sm={values?.icon ? 8.5 : 12}
                  >
                    {/* <TextField
                      type='file'
                      fullWidth
                      name='icon'
                      id='icon'
                      onChange={(e) =>
                        handleFileChange(
                          e as React.ChangeEvent<HTMLInputElement>,
                          DocumentType.PropertyFacility,
                          'icon'
                        )
                      }
                      inputProps={{ multiple: false, accept: 'image/*' }}
                      InputLabelProps={{ shrink: true }}
                      variant='outlined'
                      label={'Icon'}
                      error={Boolean(errors.icon && touched.icon)}
                      helperText={
                        Boolean(errors.icon && touched.icon) && errors.icon
                      }
                    /> */}
                    <TextField
                      fullWidth
                      variant='outlined'
                      InputLabelProps={{ shrink: true }}
                      label='Icon'
                      placeholder='Select Icon'
                      value={fileName}
                      error={Boolean(errors.icon && touched.icon)}
                      helperText={
                        Boolean(errors.icon && touched.icon) && errors.icon
                      }
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position='end'>
                            <label htmlFor='icon'>
                              <IconButton component='span'>
                                <RiUploadCloud2Fill />
                              </IconButton>
                            </label>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <input
                      id='icon'
                      name='icon'
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) =>
                        handleFileChange(
                          e as React.ChangeEvent<HTMLInputElement>,
                          DocumentType.PropertyFacility,
                          'icon'
                        )
                      }
                      accept='image/*'
                    />
                  </Grid>

                  {values?.icon && (
                    <Grid
                      item
                      xs={3.5}
                      sm={3.5}
                    >
                      <Avatar
                        imgProps={{ crossOrigin: 'anonymous' }}
                        src={getAssetURl(values?.icon)}
                        sx={{
                          borderRadius: 'unset',
                          m: 1,
                          objectFit: 'contain',
                          '& img': { objectFit: 'contain' },
                          width: { xs: '75px', sm: '100px' },
                          height: 50,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box py={2}>
                <MultipleAutoComplete
                  key={propertyCategoryIdsKey}
                  onChange={(event, newValue, reason) => {
                    if (reason === 'removeOption') {
                      setFieldValue(
                        'propertyCategoryIds',
                        newValue?.map((propertyCategory) => propertyCategory.id)
                      );
                      setPropertyCategorys((propertyCategoryInfo) =>
                        propertyCategoryInfo.filter(
                          (propertyCategory) =>
                            propertyCategory?.id === newValue[0]?.id
                        )
                      );
                    }
                    if (typeof newValue === 'string') {
                    } else if (Array.isArray(newValue)) {
                      setFieldValue(
                        'propertyCategoryIds',
                        newValue?.map(
                          (propertyCategory) => propertyCategory?.id
                        )
                      );
                      setPropertyCategorys((curr) =>
                        uniqBy([...curr, ...newValue], 'id')
                      );
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('propertyCategoryIds', []);
                    }
                  }}
                  renderOption={(props, option: PropertyCategory) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option: PropertyCategory) => option.labelEn}
                  fetchFunction={onScrollFetchPropertyCategoryIds}
                  label={'Select Property Category'}
                  onFocus={() =>
                    dispatch(
                      setSelectPropertyCategoryStatus({ status: 'idle' })
                    )
                  }
                  loading={isPropertyCategoryIdsLoading}
                  options={uniquePropertyCategoryIdsData}
                  searchFunction={searchPropertyCategoryIds}
                  value={uniquePropertyCategoryIdsData.filter((item) =>
                    values.propertyCategoryIds.includes(item.id)
                  )}
                  limitTags={4}
                  error={Boolean(
                    touched.propertyCategoryIds && errors.propertyCategoryIds
                  )}
                  helperText={
                    Boolean(
                      touched.propertyCategoryIds && errors.propertyCategoryIds
                    )
                      ? errors?.propertyCategoryIds
                      : ''
                  }
                />
              </Box>

              <Box pb={2}>
                <MultipleAutoComplete
                  key={propertySubCategoryIdsKey}
                  onChange={(event, newValue, reason) => {
                    if (reason === 'removeOption') {
                      setFieldValue(
                        'propertySubCategoryIds',
                        newValue?.map(
                          (propertySubCategory) => propertySubCategory.id
                        )
                      );
                      setPropertyCategorys((propertyCategoryInfo) =>
                        propertyCategoryInfo.filter(
                          (propertySubCategory) =>
                            propertySubCategory?.id === newValue[0]?.id
                        )
                      );
                    }
                    if (typeof newValue === 'string') {
                    } else if (Array.isArray(newValue)) {
                      setFieldValue(
                        'propertySubCategoryIds',
                        newValue?.map(
                          (propertyCategory) => propertyCategory?.id
                        )
                      );
                      if (isValidArray(propertySubCategorys)) {
                        setPropertySubCategorys((curr) =>
                          uniqBy([...curr, ...newValue], 'id')
                        );
                      } else {
                        setPropertySubCategorys(() =>
                          uniqBy([...newValue], 'id')
                        );
                      }
                      // setPropertySubCategorys((curr) =>
                      //   uniqBy([...curr, ...newValue], 'id')
                      // );
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('propertySubCategoryIds', []);
                    }
                  }}
                  renderOption={(props, option: PropertySubCategory) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option: PropertySubCategory) =>
                    option.labelEn
                  }
                  fetchFunction={onScrollFetchPropertySubCategoryIds}
                  label={'Select Property Sub Category'}
                  onFocus={() =>
                    dispatch(
                      setSelectPropertySubCategoryStatus({ status: 'idle' })
                    )
                  }
                  loading={isPropertySubCategoryIdsLoading}
                  options={uniquePropertySubCategoryIdsData}
                  searchFunction={searchPropertySubCategoryIds}
                  value={uniquePropertySubCategoryIdsData.filter((item) =>
                    values.propertySubCategoryIds.includes(item.id)
                  )}
                  limitTags={4}
                  error={Boolean(
                    touched.propertySubCategoryIds &&
                      errors.propertySubCategoryIds
                  )}
                  helperText={
                    Boolean(
                      touched.propertySubCategoryIds &&
                        errors.propertySubCategoryIds
                    )
                      ? errors?.propertySubCategoryIds
                      : ''
                  }
                />
              </Box>

              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='activeState'
                        onChange={(e) =>
                          setFieldValue('activeState', e.target.checked)
                        }
                        checked={values.activeState}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Active Status'}
                  />
                </Grid>
              </Grid>

              <Stack
                alignItems='center'
                sx={{ mt: 3 }}
                direction='row'
                justifyContent='flex-end'
                gap={1}
              >
                <Button
                  variant='outlined'
                  onClick={() => navigation('/property-facility')}
                >
                  {'Cancel'}
                </Button>

                <LoadingButton
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  {isAddMode
                    ? 'Add Property Facility'
                    : 'Edit Property Facility'}
                </LoadingButton>
              </Stack>
            </Card>
          </form>
        )}
      </Grid>
    </Grid>
  );
};

export default PropertyFacilityForm;
